import {
    isAccommodationQuantityType,
    isAccommodationQuantityTypeCountedAsGuest,
} from "@@/shared/acquire/accommodation-quantity-types";
import { isBookableOptionId } from "@@/shared/acquire/resource-reference-options/bookable-option-id";
import {
    isOrderItemAccommodation,
    isOrderItemBookableQuantity,
} from "@@/shared/orders/order-items/is-order-item";
import type { OrderItem } from "@@/shared/orders/order-items/order-item";
import { orderItemUtils } from "@@/shared/orders/order-items/order-item-utils";
import { sum } from "@@/utilities/sum";
import { filter, flatMap, pipe, sumBy } from "remeda";
import type { ProductFormField } from "../fields/product-form-field";

/**
 * Calculates the quantity for a given product form field based on the provided order items.
 *
 * The calculation logic depends on the type of the `field.quantity`:
 *
 * - If `field.quantity` is a bookable option ID, it sums the quantities of order items
 *   that match the bookable option ID.
 * - If `field.quantity` is an accommodation quantity type, it sums the quantities of
 *   order items that match the accommodation type or are counted as guests (if applicable).
 * - If `field.quantity` is `"ITEMS_IN_CART"`, it sums the quantities of order items
 *   associated with the specified `field.productId`.
 * - If `field.quantity` is a number, it is treated as a static quantity and returned directly.
 *
 * @param field - The product form field containing the quantity type and other metadata.
 * @param orderItems - The list of order items to calculate the quantity from.
 * @returns The calculated quantity based on the field's quantity type and the provided order items.
 */
const calculateProductFormFieldQuantity = (
    field: ProductFormField,
    orderItems: OrderItem[],
) => {
    if (isBookableOptionId(field.quantity)) {
        // If field.quantity is a bookable option id , find corresponding
        // bookable option id among the order items and parse the quantity
        // that needs to be summed because it's connected to the field
        const quantities = orderItems
            .filter(item => isOrderItemBookableQuantity(item))
            .filter(item => item.optionId === field.quantity)
            .map(item => item.quantity.value);
        return sum(quantities);
    }
    if (isAccommodationQuantityType(field.quantity)) {
        // If field.quantity is an accommodation quantity type, find
        // corresponding accommodation quantity type among the order items
        // and parse the quantity that needs to be summed because it's connected
        // to the field
        const quantities = pipe(
            orderItems,
            filter(item => isOrderItemAccommodation(item)),
            flatMap(item => item.quantities),
            filter(item => {
                const exactMatch = item.type.type === field.quantity;
                if (exactMatch) return true;

                // Check if the type is Guest, if so, match it against some other types as well
                if (field.quantity !== "GUEST") return false;

                // If the field quantity is "GUEST",
                // check if the item type is counted as guest
                // cause then we have a match
                return isAccommodationQuantityTypeCountedAsGuest(
                    item.type.type,
                );
            }),
            sumBy(item => item.quantity.value),
        );
        return quantities;
    }
    if (field.quantity === "ITEMS_IN_CART") {
        // If field.quantity is "ITEMS_IN_CART", find all order items
        // that are connected to the field (by productId) and sum their quantities
        const quantities = orderItems
            .filter(item => orderItemUtils.hasProduct(item, field.productId))
            .map(item => item.quantity.value);
        return sum(quantities);
    }

    // If the quantity is a number it's a static quantity
    return field.quantity;
};
export { calculateProductFormFieldQuantity };
